/**
 * "Forgot password?" — request a reset link.
 *
 * The confirmation is deliberately the same whether or not the email belongs
 * to an account. Saying "no account with that email" would let anyone probe
 * which addresses are Design Studio HQ users.
 */

import { requestPasswordReset } from '@/lib/auth';
import { esc, setPageTitle } from '@/lib/dom';
import { signInBrand } from '@/lib/host-branding';
import { authCardHeader, authErrorMessage, readLastEmail } from '@/lib/auth-card';

export interface ForgotPasswordOptions {
  hostname?: string;
  /** Shown above the form — e.g. why an emailed link didn't work. */
  notice?: string;
  onBack: () => void;
}

export function renderForgotPassword(root: HTMLElement, opts: ForgotPasswordOptions): void {
  const brand = signInBrand(opts.hostname ?? location.hostname);
  setPageTitle('Reset password', brand.titleBase);

  root.innerHTML = `
    <div class="auth">
      <form class="auth-card" id="forgot-form" novalidate>
        ${authCardHeader(brand, 'Reset your password')}
        ${opts.notice ? `<div class="auth-error" id="forgot-notice">${esc(opts.notice)}</div>` : ''}
        <p class="auth-copy">Enter the email you sign in with and we’ll send a link to choose a new password.</p>
        <label class="field">
          <span>Email</span>
          <input type="email" name="email" autocomplete="email" value="${esc(readLastEmail())}" required />
        </label>
        <div class="auth-error" id="forgot-error" hidden></div>
        <button type="submit" class="btn-primary" id="forgot-submit">Send reset link</button>
        <button type="button" class="btn-link" data-action="back-to-sign-in">Back to sign in</button>
      </form>
    </div>`;

  const form = root.querySelector<HTMLFormElement>('#forgot-form')!;
  const errBox = root.querySelector<HTMLDivElement>('#forgot-error')!;
  const submit = root.querySelector<HTMLButtonElement>('#forgot-submit')!;

  root.querySelectorAll('[data-action="back-to-sign-in"]')
    .forEach((el) => el.addEventListener('click', () => opts.onBack()));

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errBox.hidden = true;
    const email = String(new FormData(form).get('email') ?? '').trim();
    if (!email) {
      errBox.hidden = false;
      errBox.textContent = 'Enter your email address.';
      return;
    }
    submit.disabled = true;
    submit.textContent = 'Sending…';
    try {
      await requestPasswordReset(email);
      showSent(root, email, brand, opts.onBack);
    } catch (err) {
      errBox.hidden = false;
      errBox.textContent = authErrorMessage(err, 'Could not send the reset link.');
      submit.disabled = false;
      submit.textContent = 'Send reset link';
    }
  });
}

function showSent(
  root: HTMLElement, email: string, brand: ReturnType<typeof signInBrand>, onBack: () => void,
): void {
  root.innerHTML = `
    <div class="auth">
      <div class="auth-card" id="forgot-sent">
        ${authCardHeader(brand, 'Check your email')}
        <p class="auth-copy">If an account exists for <strong>${esc(email)}</strong>, a link to
        reset your password is on its way. It works once, and only for a limited time.</p>
        <p class="auth-copy muted">Nothing after a few minutes? Check your spam folder, or try again.</p>
        <button type="button" class="btn-link" data-action="back-to-sign-in">Back to sign in</button>
      </div>
    </div>`;
  root.querySelectorAll('[data-action="back-to-sign-in"]')
    .forEach((el) => el.addEventListener('click', () => onBack()));
}
